import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  updateDoc, 
  doc,
  getDoc,
  orderBy,
  deleteDoc,
  serverTimestamp,
  arrayUnion
} from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import { FaTrash, FaUserEdit, FaCrown, FaUser, FaRedo } from 'react-icons/fa';
import { resetUserDreamCounter, resetAllDreamCounters } from '../utils/adminUtils';
import { setUserType } from '../utils/userManagement';
import * as XLSX from 'xlsx';
import { FaDownload, FaFilter, FaSearch } from 'react-icons/fa';
import { FaCoins, FaUserCog, FaCheck, FaTimes } from 'react-icons/fa';


const AdminDashboard = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resetLoading, setResetLoading] = useState(false);
    const [upgradeRequests, setUpgradeRequests] = useState([]);
    const [upgradeRequestsLoading, setUpgradeRequestsLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
    const [mobileAppWaitlist, setMobileAppWaitlist] = useState([]);
    const [newsletterSignups, setNewsletterSignups] = useState([]);
    const [mobileWaitlist, setMobileWaitlist] = useState([]);
    const [newsletterFilter, setNewsletterFilter] = useState('');
    const [mobileWaitlistFilter, setMobileWaitlistFilter] = useState('');
    const [activeTab, setActiveTab] = useState('creditManagement');
    const [selectedUser, setSelectedUser] = useState(null);
    const [creditAmount, setCreditAmount] = useState(0);

    useEffect(() => {
        const checkAdminStatus = async () => {
            try {
                // Ensure user is authenticated
                const currentUser = auth.currentUser;
                if (!currentUser) {
                    console.log('No authenticated user');
                    setIsAdmin(false);
                    setLoading(false);
                    return;
                }

                // Fetch user profile directly
                const userDocRef = doc(db, 'profiles', currentUser.uid);
                const userDocSnap = await getDoc(userDocRef);

                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    const adminStatus = userData.role === 'admin';
                    
                    console.log('Admin status:', adminStatus);
                    console.log('User role:', userData.role);
                    
                    setIsAdmin(adminStatus);
                    
                    if (adminStatus) {
                        await fetchUsers();
                        await fetchUpgradeRequests();
                        await fetchNewsletterSubscribers();
                        await fetchMobileAppWaitlist();
                        await fetchNewsletterSignups();
                        await fetchMobileWaitlist();
                    }
                } else {
                    console.log('No user profile found');
                    setIsAdmin(false);
                }
            } catch (error) {
                console.error('Error checking admin status:', error);
                setIsAdmin(false);
            } finally {
                setLoading(false);
                setUpgradeRequestsLoading(false);
            }
        };

        checkAdminStatus();
    }, []);

    const fetchUsers = async () => {
        try {
            const usersRef = collection(db, 'profiles');
            const querySnapshot = await getDocs(usersRef);
            
            const fetchedUsers = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setUsers(fetchedUsers);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchUpgradeRequests = async () => {
        try {
            const requestsRef = collection(db, 'upgradeRequests');
            const q = query(
                requestsRef, 
                where('status', '==', 'pending'),
                orderBy('requestDate', 'desc')
            );
            const querySnapshot = await getDocs(q);
            
            const requests = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setUpgradeRequests(requests);
        } catch (error) {
            console.error('Error fetching upgrade requests:', error);
        }
    };


    // Manual credit addition
  const handleAddCredits = async () => {
    if (!selectedUser || creditAmount <= 0) {
      alert('Please select a user and enter a valid credit amount');
      return;
    }

    try {
      const userRef = doc(db, 'profiles', selectedUser.id);
      
      // Prepare credit history entry
      const creditHistoryEntry = {
        type: 'Admin Manual Credit',
        amount: creditAmount,
        timestamp: new Date().toISOString()
      };

      // Update user credits and credit history
      await updateDoc(userRef, {
        credits: (selectedUser.credits || 0) + creditAmount,
        creditHistory: arrayUnion(creditHistoryEntry)
      });

      // Refresh users list
      const updatedUsersSnapshot = await getDocs(collection(db, 'profiles'));
      const updatedUsersList = updatedUsersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(updatedUsersList);

      alert(`Successfully added ${creditAmount} credits to ${selectedUser.username}`);
      setSelectedUser(null);
      setCreditAmount(0);
    } catch (error) {
      console.error('Error adding credits:', error);
      alert('Failed to add credits');
    }
  };

    const fetchNewsletterSubscribers = async () => {
        try {
            const q = query(
                collection(db, 'newsletter_subscribers'), 
                orderBy('subscribedAt', 'desc')
            );
            const querySnapshot = await getDocs(q);
            const subscribers = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setNewsletterSubscribers(subscribers);
        } catch (error) {
            console.error('Error fetching newsletter subscribers:', error);
        }
    };

    const fetchMobileAppWaitlist = async () => {
        try {
            const q = query(
                collection(db, 'mobile_app_waitlist'), 
                orderBy('joinedAt', 'desc')
            );
            const querySnapshot = await getDocs(q);
            const waitlistEntries = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setMobileAppWaitlist(waitlistEntries);
        } catch (error) {
            console.error('Error fetching mobile app waitlist:', error);
        }
    };

    const fetchNewsletterSignups = async () => {
        try {
            const newsletterQuery = query(
                collection(db, 'newsletter_signups'), 
                orderBy('createdAt', 'desc')
            );
            const newsletterSnapshot = await getDocs(newsletterQuery);
            const newsletterData = newsletterSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setNewsletterSignups(newsletterData);
        } catch (error) {
            console.error('Error fetching newsletter signups:', error);
        }
    };

    const fetchMobileWaitlist = async () => {
        try {
            const mobileWaitlistQuery = query(
                collection(db, 'mobile_waitlist'), 
                orderBy('createdAt', 'desc')
            );
            const mobileWaitlistSnapshot = await getDocs(mobileWaitlistQuery);
            const mobileWaitlistData = mobileWaitlistSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setMobileWaitlist(mobileWaitlistData);
        } catch (error) {
            console.error('Error fetching mobile waitlist:', error);
        }
    };

    const handleChangeUserRole = async (userId, newRole) => {
        try {
            await setUserType(userId, newRole);
            // Update local state
            setUsers(users.map(user => 
                user.id === userId ? { ...user, role: newRole } : user
            ));
        } catch (error) {
            console.error('Error changing user role:', error);
            alert('Failed to change user role');
        }
    };

    const handleResetCounter = async (userId) => {
        try {
            await resetUserDreamCounter(userId);
            // Refresh user data
            fetchUsers();
        } catch (error) {
            console.error('Error resetting counter:', error);
        }
    };

    const handleResetAllCounters = async () => {
        if (!window.confirm('Are you sure you want to reset all dream counters?')) {
            return;
        }
        
        setResetLoading(true);
        try {
            await resetAllDreamCounters();
            // Refresh user data
            fetchUsers();
        } catch (error) {
            console.error('Error resetting all counters:', error);
        } finally {
            setResetLoading(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                await deleteDoc(doc(db, 'profiles', userId));
                setUsers(users.filter(user => user.id !== userId));
            } catch (error) {
                console.error('Error deleting user:', error);
            }
        }
    };

    const handleApproveRequest = async (request) => {
        try {
            // Update user's profile
            const userDocRef = doc(db, 'profiles', request.userId);
            await updateDoc(userDocRef, {
                role: 'pro',
                proSubscriptionDate: new Date(),
                proSubscriptionMethod: request.paymentMethod
            });

            // Update request status
            const requestDocRef = doc(db, 'upgradeRequests', request.id);
            await updateDoc(requestDocRef, {
                status: 'approved',
                processedDate: new Date()
            });

            // Refresh the list
            await fetchUpgradeRequests();
        } catch (error) {
            console.error('Error approving upgrade request:', error);
            alert('Failed to approve upgrade request');
        }
    };

    const handleRejectRequest = async (request) => {
        try {
            const requestDocRef = doc(db, 'upgradeRequests', request.id);
            await updateDoc(requestDocRef, {
                status: 'rejected',
                processedDate: new Date()
            });

            // Refresh the list
            await fetchUpgradeRequests();
        } catch (error) {
            console.error('Error rejecting upgrade request:', error);
            alert('Failed to reject upgrade request');
        }
    };

    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const exportToExcel = (data, filename) => {
        const exportData = data.map(item => ({
            Email: item.email,
            'Signup Date': item.createdAt?.toDate().toLocaleString(),
            'Signup Source': item.signupSource || 'N/A'
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Signups');

        XLSX.writeFile(workbook, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const filteredNewsletterSignups = newsletterSignups
        .filter(signup => 
            isValidEmail(signup.email) && 
            signup.email.toLowerCase().includes(newsletterFilter.toLowerCase())
        );

    const filteredMobileWaitlist = mobileWaitlist
        .filter(signup => 
            isValidEmail(signup.email) && 
            signup.email.toLowerCase().includes(mobileWaitlistFilter.toLowerCase())
        );

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div className="container mx-auto p-6 text-center">
                <h2 className="text-2xl font-bold text-red-500">
                    Access Denied
                </h2>
                <p className="text-gray-600 mt-4">
                    You do not have administrator privileges.
                </p>
            </div>
        );
    }

    // Handle upgrade request
  const handleUpgradeRequest = async (request, approve) => {
    try {
      const requestRef = doc(db, 'upgradeRequests', request.id);
      
      if (approve) {
        // Update user profile
        const userRef = doc(db, 'profiles', request.userId);
        await updateDoc(userRef, {
          role: request.requestedRole,
          credits: (request.creditsRequested || 0) + (request.currentCredits || 0),
          upgradeRequestApproved: true
        });

        // Log upgrade in credit history
        await updateDoc(userRef, {
          creditHistory: arrayUnion({
            type: 'Role Upgrade',
            fromRole: request.currentRole,
            toRole: request.requestedRole,
            creditsAdded: request.creditsRequested,
            timestamp: serverTimestamp()
          })
        });
      }

      // Delete the request
      await deleteDoc(requestRef);

      // Refresh upgrade requests
      const requestsQuery = query(
        collection(db, 'upgradeRequests'), 
        orderBy('createdAt', 'desc')
      );
      const requestsSnapshot = await getDocs(requestsQuery);
      const requestsList = requestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUpgradeRequests(requestsList);

      alert(approve 
        ? 'Upgrade request approved successfully' 
        : 'Upgrade request rejected'
      );
    } catch (error) {
      console.error('Error processing upgrade request:', error);
      alert('Failed to process upgrade request');
    }
  };


    return (
        
        <div className="container mx-auto p-6">
            {/* Users Management Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
               
                <h2 className="text-2xl font-bold mb-4 dark:text-white">Users Management</h2>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr>
                                <th className="px-6 py-3 text-left">Username</th>
                                <th className="px-6 py-3 text-left">Email</th>
                                <th className="px-6 py-3 text-left">Role</th>
                                <th className="px-6 py-3 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user) => (
                                <tr key={user.id} className="border-b dark:border-gray-700">
                                    <td className="px-6 py-4">{user.username || 'N/A'}</td>
                                    <td className="px-6 py-4">{user.email || 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <select
                                            value={user.role || 'simple'}
                                            onChange={(e) => handleChangeUserRole(user.id, e.target.value)}
                                            className="text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                                        >
                                            <option value="simple">Simple</option>
                                            <option value="pro">Pro</option>
                                            <option value="admin">Admin</option>
                                            <option value="moderator">Moderator</option>
                                        </select>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex items-center space-x-3">
                                            <button
                                                onClick={() => handleResetCounter(user.id)}
                                                className="text-purple-400 hover:text-purple-300 transition-colors"
                                                title="Reset Counter"
                                            >
                                                <FaRedo />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteUser(user.id)}
                                                className="text-red-500 hover:text-red-700 dark:hover:text-red-400 transition-colors"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Reset All Counters Button */}
            <button
                onClick={handleResetAllCounters}
                disabled={resetLoading}
                className="px-4 py-2 bg-purple-500 text-white rounded-full hover:bg-purple-600 transition-colors disabled:opacity-50 mt-4"
            >
                {resetLoading ? 'Resetting...' : 'Reset All Counters'}
            </button>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {[
            { key: 'creditManagement', label: 'Credit Management' },
            { key: 'upgradeRequests', label: 'Upgrade Requests' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-4 text-sm font-medium uppercase tracking-wide 
                ${activeTab === tab.key 
                  ? 'text-purple-600 border-b-2 border-purple-600' 
                  : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'creditManagement' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaCoins className="mr-2 text-purple-500" /> Credit Management
              </h2>
              
              {/* User Selection */}
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-medium mb-3">Select User</h3>
                <select 
                  value={selectedUser?.id || ''}
                  onChange={(e) => {
                    const user = users.find(u => u.id === e.target.value);
                    setSelectedUser(user);
                  }}
                  className="w-full p-2 border rounded text-purple-500"
                >
                  <option value="">Select a User</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.username} (Current Credits: {user.credits || 0})
                    </option>
                  ))}
                </select>
              </div>

              {/* Credit Addition */}
              {selectedUser && (
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <h3 className="text-lg font-medium mb-3">Add Credits</h3>
                  <div className="flex space-x-2 text-purple-500">
                    <input 
                      type="number" 
                      value={creditAmount}
                      onChange={(e) => setCreditAmount(Number(e.target.value))}
                      placeholder="Enter credit amount"
                      className="flex-grow p-2 border rounded"
                    />
                    <button 
                      onClick={handleAddCredits}
                      className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                    >
                      Add Credits
                    </button>
                    </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'upgradeRequests' && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <FaUserCog className="mr-2 text-purple-500" /> Upgrade Requests
              </h2>
              
              {upgradeRequests.length === 0 ? (
                <p className="text-gray-500">No pending upgrade requests</p>
              ) : (
                <div className="space-y-4">
                  {upgradeRequests.map((request) => (
                    <div 
                      key={request.id} 
                      className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg flex justify-between items-center"
                    >
                      <div>
                        <p>
                          <strong>{request.username}</strong> requests upgrade 
                          from <span className="capitalize">{request.currentRole}</span> 
                          to <span className="capitalize">{request.requestedRole}</span>
                        </p>
                        <p className="text-sm text-gray-600">
                          Credits Requested: {request.creditsRequested}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleUpgradeRequest(request, true)}
                          className="bg-green-500 text-white p-2 rounded hover:bg-green-600"
                        >
                          <FaCheck />
                        </button>
                        <button 
                          onClick={() => handleUpgradeRequest(request, false)}
                          className="bg-red-500 text-white p-2 rounded hover:bg-red-600"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>   

            {/* Upgrade Requests Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mt-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">Upgrade Requests</h2>
                {upgradeRequests.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-300">No pending upgrade requests.</p>
                ) : (
                    <div className="space-y-4">
                        {upgradeRequests.map((request) => (
                            <div 
                                key={request.id} 
                                className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg flex justify-between items-center"
                            >
                                <div>
                                    <p className="font-semibold dark:text-white">{request.username}</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                        Current Role: {request.currentRole}
                                        <br />
                                        Requested Role: {request.requestedRole}
                                        <br />
                                        Payment Method: {request.paymentMethod}
                                        <br />
                                        Request Date: {request.requestDate?.toDate()?.toLocaleString()}
                                    </p>
                                </div>
                                <div className="flex space-x-2">
                                    <button 
                                        onClick={() => handleApproveRequest(request)}
                                        className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                                    >
                                        Approve
                                    </button>
                                    <button 
                                        onClick={() => handleRejectRequest(request)}
                                        className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                                    >
                                        Reject
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Newsletter Subscribers Table */}
            {/* <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mt-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                    Newsletter Subscribers
                </h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                        <thead>
                            <tr>
                                <th className="px-6 py-3">Email</th>
                                <th className="px-6 py-3">Subscribed At</th>
                            </tr>
                        </thead>
                        <tbody>
                            {newsletterSubscribers.map((subscriber) => (
                                <tr 
                                    key={subscriber.id} 
                                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                                >
                                    <td className="px-6 py-4">{subscriber.email}</td>
                                    <td className="px-6 py-4">
                                        {subscriber.subscribedAt?.toDate?.().toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div> */}

            {/* Mobile App Waitlist Table */}
            {/* <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mt-8">
                <h2 className="text-2xl font-bold mb-4 dark:text-white">
                    Mobile App Waitlist
                </h2>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                        <thead>
                            <tr>
                                <th className="px-6 py-3">Email</th>
                                <th className="px-6 py-3">Joined At</th>
                            </tr>
                        </thead>
                        <tbody>
                            {mobileAppWaitlist.map((entry) => (
                                <tr 
                                    key={entry.id} 
                                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                                >
                                    <td className="px-6 py-4">{entry.email}</td>
                                    <td className="px-6 py-4">
                                        {entry.joinedAt?.toDate?.().toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div> */}

            {/* Newsletter Signups */}
            <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mt-8">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold">Newsletter Signups</h3>
                    <div className="flex items-center space-x-4">
                        {/* Search Filter */}
                        <div className="relative">
                            <input 
                                type="text" 
                                placeholder="Filter emails"
                                value={newsletterFilter}
                                onChange={(e) => setNewsletterFilter(e.target.value)}
                                className="pl-8 pr-3 py-2 border rounded"
                            />
                            <FaSearch className="absolute left-2 top-3 text-gray-400" />
                        </div>
                        
                        {/* Export Button */}
                        <button 
                            onClick={() => exportToExcel(filteredNewsletterSignups, 'newsletter_signups')}
                            className="bg-green-500 text-white px-4 py-2 rounded flex items-center"
                        >
                            <FaDownload className="mr-2" /> Export
                        </button>
                    </div>
                </div>
                
                <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                <thead>
                            <tr>
                                <th className="p-3 text-left">Email</th>
                                <th className="p-3 text-left">Signup Date</th>
                                <th className="p-3 text-left">Signup Source</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredNewsletterSignups.map((signup) => (
                                <tr key={signup.id} className="border-b">
                                    <td className="p-3">{signup.email}</td>
                                    <td className="p-3">
                                        {signup.createdAt?.toDate().toLocaleString()}
                                    </td>
                                    <td className="p-3">
                                        {signup.signupSource || 'Unknown'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Mobile Waitlist */}
            <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mt-8">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold">Mobile App Waitlist</h3>
                    <div className="flex items-center space-x-4">
                        {/* Search Filter */}
                        <div className="relative">
                            <input 
                                type="text" 
                                placeholder="Filter emails"
                                value={mobileWaitlistFilter}
                                onChange={(e) => setMobileWaitlistFilter(e.target.value)}
                                className="pl-8 pr-3 py-2 border rounded"
                            />
                            <FaSearch className="absolute left-2 top-3 text-gray-400" />
                        </div>
                        
                        {/* Export Button */}
                        <button 
                            onClick={() => exportToExcel(filteredMobileWaitlist, 'mobile_waitlist')}
                            className="bg-green-500 text-white px-4 py-2 rounded flex items-center"
                        >
                            <FaDownload className="mr-2" /> Export
                        </button>
                    </div>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                        <thead>
                            <tr>
                                <th className="p-3 text-left">Email</th>
                                <th className="p-3 text-left">Signup Date</th>
                                <th className="p-3 text-left">Signup Source</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredMobileWaitlist.map((signup) => (
                                <tr key={signup.id} className="border-b">
                                    <td className="p-3">{signup.email}</td>
                                    <td className="p-3">
                                        {signup.createdAt?.toDate().toLocaleString()}
                                    </td>
                                    <td className="p-3">
                                        {signup.signupSource || 'Unknown'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default AdminDashboard;
